import './styles/CompanyLogin.scss';

import { useMutation, useQuery } from '@apollo/client';
import { Button, Grid, TextField } from '@mui/material';

import { Field, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import LOGIN_MUTATION from 'gql/mutations/login.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface Props {
  closeHandler: Function;
  setRegisterModalOpen: Function;
}
export function CompanyLoginModal(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [submitting, setSubmitting] = useState(false);

  const [login] = useMutation(LOGIN_MUTATION);
  const { refetch: getUser } = useQuery<any, any>(USER_QUERY, {
    skip: true,
    context: { clientName: 'federation' }
  });

  const handleLogin = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await login({ variables: values });
      localStorage.setItem('jwt-token', data.login.access_token);
      localStorage.setItem('refresh-token', data.login.refresh_token);
      await getUser();
      //! navigate not working -> should be used in useEffect, but maybe still effects internal states -> navigate + href
      if (state) {
        navigate(state?.from?.pathname, { replace: true });
        window.location.href = state?.from?.pathname;
      } else {
        const locationAfterLogin = localStorage.getItem('redirect-after-login');
        if (locationAfterLogin) {
          localStorage.removeItem('redirect-after-login');
          navigate(locationAfterLogin, { replace: true });
          window.location.href = locationAfterLogin;
        } else {
          navigate('/', { replace: true });
          window.location.href = '/';
        }
      }
      props.closeHandler();
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.login.alerts.loginFailed())));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid container className="companyLogin">
      <Grid className="companyLogin__headline" item xs={12}>
        {t(translations.pages.login.loginSelection.companyLogin.title()).toUpperCase()}
      </Grid>
      <Grid className="companyLogin__login" item xs={12}>
        {t(translations.pages.login.title())}
      </Grid>
      <Grid className="companyLogin__form" item xs={12}>
        <Formik
          initialValues={{ email: '', password: '' }}
          isInitialValid={false}
          onSubmit={values => handleLogin(values)}
          validationSchema={yup.object().shape({
            email: yup.string().email().required(),
            password: yup.string().required()
          })}
        >
          {({ handleSubmit, errors, isValid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label={t(translations.common.email())}
                    name="email"
                    autoFocus
                    error={!!errors?.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t(translations.common.password())}
                    type="password"
                    error={!!errors?.password}
                    autoComplete="on"
                  />
                </Grid>
                <Grid className="companyLogin__form__forgot" item xs={12}>
                  <Link to="/forgot" tabIndex={-1}>
                    {t(translations.pages.login.forgot())}
                  </Link>
                </Grid>
                <Grid className="companyLogin__form__login" item xs={12}>
                  <span style={{ position: 'relative' }}>
                    <Button
                      className="companyLogin__form__login__button"
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={submitting || !isValid}
                    >
                      {t(translations.pages.login.title())}
                    </Button>
                    {submitting && <SubmittingLoader />}
                  </span>
                </Grid>
                <Grid className="companyLogin__form__or" item xs={12}>
                  {t(translations.pages.login.or()).toUpperCase()}
                </Grid>
                <Grid className="companyLogin__form__register" item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    color="primary"
                    onClick={() => {
                      props.closeHandler();
                      props.setRegisterModalOpen(true);
                    }}
                  >
                    {t(translations.pages.login.registerButton())}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}
